import { memo, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useData } from "../hooks/useData";
import CategoryToolbar from "./CategoryToolbar";
import UploadToolbar from "./UploadToolbar";
import TemplatesToolbar from "./TemplatesToolbar";
import ITextToolbar from "./ITextToolbar";
import PixabayGalleryToolbar from "./PixabayGalleryToolbar";
import ShapesToolbar from "./ShapesToolbar";
import DrawingToolbar from "./DrawingToolbar";
import BackgroundToolbar from "./BackgroundToolbar";
import ImageFilterToolbar from "./ImageFilterToolbar"; // Added import
import ITextToolbarSelected from "./ITextToolbarSelected"; // Added import

const ContextualDrawer = ({ canvas }) => {
  const { selectedTool, selectedObject } = useData();
  const [lastAction, setLastAction] = useState("init"); // Track the last user action

  // Log selectedObject.type whenever selectedObject changes and update lastAction
  useEffect(() => {
    if (selectedObject) {
      console.log(
        "ContextualDrawer: Selected object type",
        selectedObject.type
      );
      setLastAction("object"); // Update last action to object selection
    } else {
      console.log("ContextualDrawer: No object selected");
    }
  }, [selectedObject]); // Trigger on changes to selectedObject

  // Update lastAction when selectedTool changes
  useEffect(() => {
    if (selectedTool) {
      console.log("ContextualDrawer: Selected tool", selectedTool);
      setLastAction("tool"); // Update last action to tool selection
    }
  }, [selectedTool]); // Trigger on changes to selectedTool

  const renderToolbar = () => {
    // On app initialization, render CategoryToolbar
    if (lastAction === "init") {
      return <CategoryToolbar canvas={canvas} />;
    }

    // If the last action was a tool change, render based on selectedTool
    if (lastAction === "tool" && selectedTool) {
      switch (selectedTool) {
        case "category":
          return <CategoryToolbar canvas={canvas} />;
        case "upload":
          return <UploadToolbar canvas={canvas} />;
        case "templates":
          return <TemplatesToolbar canvas={canvas} />;
        case "text":
          return <ITextToolbar canvas={canvas} />;
        case "images":
          return <PixabayGalleryToolbar canvas={canvas} />;
        case "shapes":
          return <ShapesToolbar canvas={canvas} />;
        case "draw":
          return <DrawingToolbar canvas={canvas} />;
        case "background":
          return <BackgroundToolbar canvas={canvas} />;
        default:
          return <CategoryToolbar canvas={canvas} />;
      }
    }

    // If the last action was an object change, render based on selectedObject.type
    if (lastAction === "object" && selectedObject) {
      switch (selectedObject.type) {
        case "rect":
        case "circle":
        case "triangle":
        case "ellipse":
        case "line":
        case "polygon":
          return <ShapesToolbar canvas={canvas} />;
        case "i-text": // Added case for image type
          return <ITextToolbarSelected canvas={canvas} />;
        case "image": // Added case for image type
          return <ImageFilterToolbar canvas={canvas} />;
        case "path":
          return <DrawingToolbar canvas={canvas} />;
        default:
          return <CategoryToolbar canvas={canvas} />;
      }
    }

    // Default case: render CategoryToolbar if no conditions are met
    return <CategoryToolbar canvas={canvas} />;
  };

  return (
    <Box
      sx={{
        width: 390, // Explicitly set to 390px
        minWidth: 390, // Prevent shrinking
        maxWidth: 390, // Prevent growing
        height: "100%",
        height: 664,
        minHeight: 664,
        maxHeight: 664,
        backgroundColor: "#fff",
        boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.1)",
        p: 2,
        mt: 2,
        ml: 5,
        mr: 2,
        fontFamily: "Roboto, sans-serif",
        overflowY: "auto",
      }}
    >
      {renderToolbar()}
    </Box>
  );
};

export default memo(ContextualDrawer);
